import type { ReactElement } from "react";

import { FiltersConfigProvider } from "app/pages/sales-intelligence/modules/advanced-search-filtering/context/FiltersConfigContext";
import { AppsFlowProvider } from "app/pages/sales-intelligence/modules/apps-search-filters/context/AppsFlowContext";
import { useAppsFlowStrategyFromFilters } from "app/pages/sales-intelligence/modules/apps-search-filters/hooks/useAppsFlowStrategyFromFilters";
import { ScoringRuleWizardHost } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleWizardHost";
import { useApplyInitialFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useApplyInitialFilters";
import useWorkspacesCall from "app/pages/sales-intelligence/sub-modules/common/hooks/useWorkspacesCall";
import { useIntegrationsSyncCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/integration-sync/useIntegrationsSyncCall";
import { useAllCrmMappingFieldsCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/useAllCrmMappingFieldsCall";
import { useAllCrmStatusesCall } from "app/pages/sales-intelligence/sub-modules/management/hooks/useAllCrmStatusesCall";
import useSuppressionListsCall from "app/pages/sales-intelligence/sub-modules/management/hooks/useSuppressionListsCall";
import withSignalsManagement from "app/pages/sales-intelligence/sub-modules/signals/hoc/withSignalsManagement";

import withListParamsProvider from "../../hoc/withListParamsProvider";
import withNotFoundListGuard from "../../hoc/withNotFoundListGuard";
import { useFiltersConfig } from "../../hooks/useFiltersConfig";

import StaticListContent from "./StaticListContent";

const StaticListPageRootContent = (): ReactElement => {
    const { filtersList, status, filtersSearchConfigList, categoriesList } = useFiltersConfig();

    // Defaults are written once here at the page root; a static list has no URL seed.
    useApplyInitialFilters({ filtersList, status });

    return (
        <FiltersConfigProvider
            filtersList={filtersList}
            status={status}
            filtersSearchConfigList={filtersSearchConfigList}
            categoriesList={categoriesList}
        >
            <StaticListContent />
            <ScoringRuleWizardHost />
        </FiltersConfigProvider>
    );
};

const StaticListPageRootContainer = (): ReactElement => {
    useWorkspacesCall();
    useAllCrmStatusesCall();
    useAllCrmMappingFieldsCall();
    useIntegrationsSyncCall();
    useSuppressionListsCall();

    const appsFlowStrategy = useAppsFlowStrategyFromFilters();

    return (
        <AppsFlowProvider value={appsFlowStrategy}>
            <StaticListPageRootContent />
        </AppsFlowProvider>
    );
};

export default withListParamsProvider(withNotFoundListGuard(withSignalsManagement(StaticListPageRootContainer)));
